/**
 * 🚜 Farm v1 — Chits-powered crops, chores, friend revives, soil upgrades.
 *
 * Four tabs:
 *   • Your Farm   — plot grid, plant/harvest/throw/chore
 *   • Seed Shop   — buy seeds w/ Chits
 *   • Friend Farms — chore/revive friends' plots
 *   • Upgrades    — Soil Lv 1→5 (Omni Dollars)
 *
 * Live ticker on each plot for ripe/wilt/revive deadlines.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { GrainHousePanel } from "@/components/GrainHouseAndMines";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Visual map for each crop status — drives the big emoji icon in each plot.
const STATUS_ICON = {
  empty: "🟫",
  growing: "🌱",
  ripe: "✨",
  wilted: "💀",
  fissure: "🌋",
};

// Format seconds → "1h 23m" / "5m 12s" / "—"
function fmt(secs) {
  if (secs == null || secs <= 0) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// Tick counter — re-renders countdowns every second
function useTick() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return tick;
}

export default function FarmPage() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState(null);
  const [friends, setFriends] = useState([]);
  const [tab, setTab] = useState("farm");
  const [busy, setBusy] = useState(false);
  const [selectedSeed, setSelectedSeed] = useState("sprout");
  const tick = useTick();

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/farm/state`);
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load farm.");
    }
  }, []);

  const refreshFriends = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/farm/friends`);
      setFriends(r.data?.farms || []);
    } catch (e) {
      // Silent — friends tab is optional
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    refresh();
    refreshFriends();
  }, [authLoading, user, refresh, refreshFriends]);

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400">
          🔒 Sign in to access your farm.
        </div>
        <Footer />
      </div>
    );
  }
  if (!state) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-neutral-500">Loading farm…</div>
        <Footer />
      </div>
    );
  }

  const nextUnlock = state.next_plot_unlock;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200" data-testid="farm-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Hero strip */}
        <div className="mb-6 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#1a1208] via-[#0A0A0A] to-[#1a0820] p-5 shadow-[0_0_30px_rgba(212,175,55,0.15)]">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-3xl font-black tracking-wide" style={{ fontFamily: 'Anton, sans-serif' }}>
                🚜 <span className="text-amber-400">YOUR FARM</span>
              </div>
              <div className="text-xs text-neutral-400 mt-1">
                Plant 🪙 Chits seeds · harvest spices, food &amp; rare eggs · feed Dave's Kitchen
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Pill icon="🪙" label="Chits" value={state.wallet.chits} testid="wallet-chits" />
              <Pill icon="🌾" label="Safe" value={state.wallet.grainhouse_chits || 0} testid="wallet-grainhouse" />
              <Pill icon="🪙" label="Omni $" value={state.wallet.omni_dollars} testid="wallet-od" />
              <Pill icon="🍚" label="Spirit" value={state.wallet.brawler_spirit} testid="wallet-spirit" />
            </div>
          </div>

          {/* Progression strip */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Farm Lv"
                  value={`Lv ${state.farm_level || 1}`}
                  sub={state.next_farm_level
                    ? `+1 at ${state.next_farm_level.at_xp} XP (${state.next_farm_level.xp_to_go} to go)`
                    : `MAX (${state.max_farm_level})`}
                  testid="stat-farm-level" />
            <Stat label="Farm XP" value={state.farm_xp} sub="lifetime" testid="stat-xp" />
            <Stat label="Plots" value={`${state.plot_count} / ${state.max_plots}`}
                  sub={nextUnlock ? `+1 at ${nextUnlock.at_xp} XP (${nextUnlock.xp_to_go} to go)` : "max unlocked"}
                  testid="stat-plots" />
            <Stat label="Soil"
                  value={`Lv ${state.soil_level}`}
                  sub={`${(state.soil_yield_mult).toFixed(2)}× yield`}
                  testid="stat-soil" />
            <Stat label="Chores today"
                  value={`${state.chore_count_pt} / ${state.chore_daily_cap}`}
                  sub={`+${state.chore_xp_per_action} XP each · resets PT`}
                  testid="stat-chores" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 border-b border-neutral-800 overflow-x-auto">
          <TabBtn id="farm"     label="🌱 Your Farm"     active={tab === "farm"}     onClick={() => setTab("farm")} testid="tab-farm" />
          <TabBtn id="grainhouse" label="🌾 GrainHouse" active={tab === "grainhouse"} onClick={() => setTab("grainhouse")} testid="tab-grainhouse" />
          <TabBtn id="shop"     label="🛒 Seed Shop"     active={tab === "shop"}     onClick={() => setTab("shop")} testid="tab-shop" />
          <TabBtn id="workshop" label="🛠️ Workshop"     active={tab === "workshop"} onClick={() => setTab("workshop")} testid="tab-workshop" />
          <TabBtn id="friends"  label={`👥 Friend Farms${friends.length ? ` (${friends.length})` : ""}`}
                  active={tab === "friends"} onClick={() => { setTab("friends"); refreshFriends(); }} testid="tab-friends" />
          <TabBtn id="upgrades" label="🪨 Upgrades"     active={tab === "upgrades"} onClick={() => setTab("upgrades")} testid="tab-upgrades" />
        </div>

        {tab === "farm" && (
          <FarmGrid
            state={state}
            tick={tick}
            busy={busy}
            setBusy={setBusy}
            refresh={refresh}
            selectedSeed={selectedSeed}
            setSelectedSeed={setSelectedSeed}
            isVIP={user?.role === "owner" || !!user?.is_premium}
          />
        )}
        {tab === "grainhouse" && (
          <GrainHousePanel refreshParent={refresh} />
        )}
        {tab === "shop" && (
          <SeedShop state={state} setState={setState} busy={busy} setBusy={setBusy} refresh={refresh} />
        )}
        {tab === "workshop" && (
          <WorkshopPanel busy={busy} setBusy={setBusy} refreshFarm={refresh} />
        )}
        {tab === "friends" && (
          <FriendFarms friends={friends} tick={tick} busy={busy} setBusy={setBusy}
                       refresh={refreshFriends} refreshMine={refresh} />
        )}
        {tab === "upgrades" && (
          <SoilUpgrades state={state} busy={busy} setBusy={setBusy} refresh={refresh} />
        )}
      </div>
      <Footer />
    </div>
  );
}

/* ───────── Building blocks ───────── */
function Pill({ icon, label, value, testid }) {
  return (
    <div className="px-3 py-1.5 rounded-full bg-black/40 border border-neutral-700 text-sm flex items-center gap-1.5"
         data-testid={testid}>
      <span>{icon}</span>
      <span className="font-bold text-amber-300">{value}</span>
      <span className="text-neutral-500 text-xs">{label}</span>
    </div>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-neutral-800 bg-black/40 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-xl font-bold text-amber-200" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
      {sub && <div className="text-[10px] text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function TabBtn({ label, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className={`px-4 py-2 text-sm font-bold border-b-2 transition whitespace-nowrap ${
              active ? "border-amber-400 text-amber-200" : "border-transparent text-neutral-500 hover:text-neutral-300"
            }`}>
      {label}
    </button>
  );
}

/* ───────── Your Farm tab ───────── */
function FarmGrid({ state, tick, busy, setBusy, refresh, selectedSeed, setSelectedSeed, isVIP }) {
  const seedInventory = state.seed_inventory || {};
  const seeds = state.seeds || {};

  // ⭐ VIP bulk actions — Grab All / Plant All (owner-canon Feb 2026)
  const ripePlotCount = useMemo(
    () => (state.plots || []).filter(
      (p) => p.crop && !p.fissure && (p.crop.status === "ripe" || (p.crop.ripens_at && Date.parse(p.crop.ripens_at) <= Date.now()))
    ).length,
    [state.plots]
  );
  const emptyPlotCount = useMemo(
    () => (state.plots || []).filter((p) => !p.crop && !p.fissure).length,
    [state.plots]
  );
  const selectedSeedCount = seedInventory[selectedSeed] || 0;

  const onHarvestAll = async () => {
    if (busy) return;
    if (ripePlotCount === 0) {
      toast.info("No ripe plots to harvest.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/harvest-all`);
      const d = r.data || {};
      const parts = [
        `🎉 Grabbed ${d.harvested_count}/${d.attempted_count} plots`,
        `+${d.chits_awarded || 0} 🪙`,
      ];
      if (d.farm_xp_awarded) parts.push(`+${d.farm_xp_awarded} Farm XP`);
      if (d.daves_eggs_won) parts.push(`+${d.daves_eggs_won} 🥚`);
      toast.success(parts.join(" · "));
      if (d.stopped_early_reason) toast.warning(`Stopped early: ${d.stopped_early_reason}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grab All failed.");
    } finally {
      setBusy(false);
    }
  };

  const onPlantAll = async () => {
    if (busy) return;
    if (!selectedSeed) {
      toast.error("Select a seed first.");
      return;
    }
    if (emptyPlotCount === 0) {
      toast.info("No empty plots to plant on.");
      return;
    }
    if (selectedSeedCount === 0) {
      toast.error(`You don't have any ${seeds[selectedSeed]?.label || "seeds"}.`);
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/plant-all`, { seed_type: selectedSeed });
      const d = r.data || {};
      const parts = [`🌱 Planted ${d.planted_count}/${d.attempted_count} plots (${d.seed_label})`];
      if (d.farm_xp_awarded) parts.push(`+${d.farm_xp_awarded} Farm XP`);
      toast.success(parts.join(" · "));
      if (d.stopped_early_reason) toast.warning(`Stopped early: ${d.stopped_early_reason}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plant All failed.");
    } finally {
      setBusy(false);
    }
  };

  // 🧪🧬 Special plots (Plot XI · Plot XII) — Farm Lv 20 unlock, phase 1
  const [specialPlots, setSpecialPlots] = useState(null);
  const refreshSpecialPlots = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/farm/special-plots/state`);
      setSpecialPlots(r.data || null);
    } catch { /* silent — non-critical panel */ }
  }, []);
  useEffect(() => { refreshSpecialPlots(); }, [refreshSpecialPlots, state?.farm_level]);

  const onUpgradeMutation = async (plot_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/special-plots/upgrade-mutation`, { plot_id });
      toast.success(`${r.data?.label || "Mutation"} unlocked — now ${r.data?.new_rate_percent}%!`);
      refresh();
      refreshSpecialPlots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed.");
    } finally {
      setBusy(false);
    }
  };

  const [recipePickerFor, setRecipePickerFor] = useState(null);   // plot_id or null
  const onOpenRecipePicker = (plot_id) => setRecipePickerFor(plot_id);
  const onCloseRecipePicker = () => setRecipePickerFor(null);

  const onPlantSpecial = async (plot_id, recipe_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/special-plots/plant`, { plot_id, recipe_id });
      toast.success(`🌱 Planted ${r.data?.recipe_label} (${r.data?.grow_hours}h · +${r.data?.plot_xp_awarded} Plot XP · +${r.data?.farm_xp_awarded} Farm XP)`);
      onCloseRecipePicker();
      refresh();
      refreshSpecialPlots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plant failed.");
    } finally {
      setBusy(false);
    }
  };

  const onHarvestSpecial = async (plot_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/special-plots/harvest`, { plot_id });
      const parts = [`🎉 +${r.data?.yield_qty} ${r.data?.yield_label}`];
      if (r.data?.mutation_triggered) {
        parts.push(`⚡ MUTATION! (${r.data.mutation_rate_percent}%)`);
        if (r.data.bonus_loot) parts.push(`+${r.data.bonus_loot.qty} ${r.data.bonus_loot.key}`);
      }
      parts.push(`+${r.data?.plot_xp_awarded} Plot XP · +${r.data?.farm_xp_awarded} Farm XP`);
      toast.success(parts.join(" · "));
      refresh();
      refreshSpecialPlots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Harvest failed.");
    } finally {
      setBusy(false);
    }
  };

  const onPlant = async (plot_id) => {
    if (busy) return;
    if ((seedInventory[selectedSeed] || 0) <= 0) {
      toast.error(`No ${seeds[selectedSeed]?.label || "seeds"} in inventory — visit the 🛒 Seed Shop.`);
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/farm/plant`, { plot_id, seed_type: selectedSeed });
      toast.success(`${seeds[selectedSeed]?.icon} Planted!`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plant failed.");
    } finally {
      setBusy(false);
    }
  };

  const onChore = async (plot_id, chore_type) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/chore`, { plot_id, chore_type });
      toast.success(`+${r.data.xp_awarded} Farm XP (${r.data.chore_count_pt}/${r.data.chore_daily_cap})`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chore failed.");
    } finally {
      setBusy(false);
    }
  };

  const onHarvest = async (plot_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/harvest`, { plot_id });
      const drops = r.data.drops || {};
      if (r.data.daves_egg) {
        toast.success("👨‍🍳 LEGENDARY — DAVE'S EGG dropped from your Phoenix Seed!", { duration: 6000 });
      } else {
        const parts = Object.entries(drops).map(([k, v]) => `+${v} ${k}`).join(" · ");
        toast.success(`✨ Harvested! ${parts}`);
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Harvest failed.");
    } finally {
      setBusy(false);
    }
  };

  const onThrow = async (plot_id) => {
    if (busy) return;
    if (!window.confirm("Throw this crop away? It will be permanently gone.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/farm/throw-away`, { plot_id });
      toast.success("🗑️ Cleared.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    } finally {
      setBusy(false);
    }
  };

  // 🌿 Consume a Lazarus Spore to revive one of the player's OWN wilted crops.
  const onSelfRevive = async (plot_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/self-revive`, { plot_id });
      toast.success(`🌿 ${r.data?.seed_label || "Crop"} revived! +${r.data?.xp_awarded || 2} Farm XP · ${r.data?.spores_remaining ?? 0} Spores left`);
      refresh();
      refreshSpecialPlots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revive failed.");
    } finally {
      setBusy(false);
    }
  };

  // 🌿 Apply a Three Sisters Coolant to a 🌋 fissure plot (organic recovery path)
  const onApplyCoolant = async (plot_id) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/farm/workshop/apply-coolant`, { plot_id });
      toast.success("🌿 Three Sisters Coolant applied — fissure restored!");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply coolant.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {/* Seed selector strip */}
      <div className="mb-4 rounded-xl border border-neutral-800 bg-black/30 p-3">
        <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Planting</div>
        <div className="flex flex-wrap gap-2">
          {Object.values(seeds).map((s) => {
            const count = seedInventory[s.id] || 0;
            const active = selectedSeed === s.id;
            return (
              <button key={s.id} data-testid={`seed-pick-${s.id}`}
                      onClick={() => setSelectedSeed(s.id)}
                      disabled={count <= 0}
                      className={`px-3 py-2 rounded-lg border text-sm transition ${
                        active ? "border-amber-400 bg-amber-500/10 text-amber-100"
                               : count > 0 ? "border-neutral-700 hover:border-neutral-500"
                                           : "border-neutral-900 opacity-50 cursor-not-allowed"
                      }`}>
                <span className="text-lg">{s.icon}</span>{" "}
                <span className="font-bold">{s.label}</span>{" "}
                <span className="text-neutral-500">x{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 👨‍🌾 Ranchhand Automation Panel (Feb 2026) */}
      <RanchhandPanel refreshFarm={refresh} isVIP={isVIP} initialRanchhand={state.ranchhand} />

      {/* 🌾 Bulk Actions — Grab All / Plant All (FREE for everyone · Feb 2026) */}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="farm-bulk-actions">
        <button
          data-testid="grab-all-btn"
          disabled={busy || ripePlotCount === 0}
          onClick={onHarvestAll}
          title={
            ripePlotCount === 0
              ? "No ripe plots to grab."
              : `Grab all ${ripePlotCount} ripe plot${ripePlotCount === 1 ? "" : "s"}.`
          }
          className={`px-4 py-2 rounded-lg text-sm font-bold border transition flex items-center gap-2 ${
            ripePlotCount === 0
              ? "border-neutral-800 bg-black/40 text-neutral-500 cursor-not-allowed"
              : "border-emerald-500 bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30"
          } disabled:cursor-not-allowed`}
        >
          <span>🧺 Grab All</span>
          {ripePlotCount > 0 && (
            <span className="text-xs bg-black/40 rounded-full px-2 py-0.5">{ripePlotCount}</span>
          )}
        </button>
        <button
          data-testid="plant-all-btn"
          disabled={busy || emptyPlotCount === 0 || !selectedSeed || selectedSeedCount === 0}
          onClick={onPlantAll}
          title={
            emptyPlotCount === 0
              ? "No empty plots."
              : !selectedSeed
              ? "Select a seed first."
              : selectedSeedCount === 0
              ? `You don't have any ${seeds[selectedSeed]?.label || "seeds"}.`
              : `Plant ${seeds[selectedSeed]?.label} on all ${emptyPlotCount} empty plot${emptyPlotCount === 1 ? "" : "s"}.`
          }
          className={`px-4 py-2 rounded-lg text-sm font-bold border transition flex items-center gap-2 ${
            (emptyPlotCount === 0 || !selectedSeed || selectedSeedCount === 0)
              ? "border-neutral-800 bg-black/40 text-neutral-500 cursor-not-allowed"
              : "border-teal-500 bg-teal-500/20 text-teal-100 hover:bg-teal-500/30"
          } disabled:cursor-not-allowed`}
        >
          <span>🌱 Plant All</span>
          {emptyPlotCount > 0 && (
            <span className="text-xs bg-black/40 rounded-full px-2 py-0.5">{emptyPlotCount}</span>
          )}
        </button>
      </div>

      {/* Plot grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {state.plots.map((p) => (
          <PlotCard key={p.plot_id} plot={p} tick={tick} busy={busy}
                    onPlant={() => onPlant(p.plot_id)}
                    onHarvest={() => onHarvest(p.plot_id)}
                    onChore={(t) => onChore(p.plot_id, t)}
                    onThrow={() => onThrow(p.plot_id)}
                    onSelfRevive={() => onSelfRevive(p.plot_id)}
                    sporeCount={state.special_inventory?.lazarus_spore || 0} />
        ))}
        {/* Locked plots preview */}
        {state.next_plot_unlock && (
          <div className="rounded-xl border-2 border-dashed border-neutral-800 bg-black/20 p-4 flex flex-col items-center justify-center text-center min-h-[180px]"
               data-testid="locked-plot-preview">
            <div className="text-3xl opacity-30">🔒</div>
            <div className="text-xs text-neutral-500 mt-2">
              Plot #{state.next_plot_unlock.plot_count}
            </div>
            <div className="text-[10px] text-amber-400 mt-1">
              {state.next_plot_unlock.xp_to_go} XP to go
            </div>
          </div>
        )}
      </div>

      {/* 🧪🧬 Special Plots (Plot XI · Plot XII) — Farm Lv 20 unlock */}
      <SpecialPlotsSection
        specialPlots={specialPlots}
        busy={busy}
        tick={tick}
        onUpgradeMutation={onUpgradeMutation}
        onOpenRecipePicker={onOpenRecipePicker}
        onHarvestSpecial={onHarvestSpecial}
      />
      {recipePickerFor && (
        <SpecialRecipePickerModal
          plot={(specialPlots?.plots || {})[recipePickerFor]}
          onClose={onCloseRecipePicker}
          onPlant={(recipe_id) => onPlantSpecial(recipePickerFor, recipe_id)}
          busy={busy}
          wallet={specialPlots?.wallet}
        />
      )}

      {/* Harvest pantry preview */}
      <div className="mt-6 rounded-xl border border-neutral-800 bg-black/30 p-4">
        <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
          🍳 Pantry (feeds Dave's Kitchen)
        </div>
        <div className="flex flex-wrap gap-3">
          <PantryChip icon="🌶️" label="Spices" value={state.harvest_inventory?.spice || 0} testid="pantry-spice" />
          <PantryChip icon="🥕" label="Food" value={state.harvest_inventory?.food || 0} testid="pantry-food" />
          <PantryChip icon="🥚" label="Rare Eggs" value={state.harvest_inventory?.rare_egg || 0} testid="pantry-rare-egg" />
          <PantryChip icon="👨‍🍳" label="Dave's Eggs" value={state.harvest_inventory?.daves_egg || 0} testid="pantry-daves-egg" />
          <PantryChip icon="🌽" label="Cornmeal"    value={state.harvest_inventory?.cornmeal || 0}    testid="pantry-cornmeal" />
          <PantryChip icon="🫘" label="Bean Mash"   value={state.harvest_inventory?.bean_mash || 0}   testid="pantry-bean-mash" />
          <PantryChip icon="🎃" label="Squash Pulp" value={state.harvest_inventory?.squash_pulp || 0} testid="pantry-squash-pulp" />
        </div>
      </div>
    </div>
  );
}

function PantryChip({ icon, label, value, testid }) {
  return (
    <div className="px-3 py-2 rounded-lg border border-neutral-700 bg-black/40 text-sm flex items-center gap-2" data-testid={testid}>
      <span className="text-lg">{icon}</span>
      <div>
        <div className="font-bold text-amber-200">{value}</div>
        <div className="text-[10px] text-neutral-500">{label}</div>
      </div>
    </div>
  );
}

/* ───────── 🧪🧬 Special Plots Section — Plot XI (Crucible) + XII (Nexus) ─────────
 * Auto-unlocks at Farm Lv 20. Renders below the standard 10-plot grid, above
 * the Pantry — per owner spec (Feb 2026).
 *
 * Phase 1: locked-state overlay + unlocked cards with progression + mutation
 * upgrade ladder. Cultivate/Synthesize action buttons are visible-but-inert
 * (splicing menu ships in Phase 2).
 */
function SpecialPlotsSection({ specialPlots, busy, tick, onUpgradeMutation, onOpenRecipePicker, onHarvestSpecial }) {
  if (!specialPlots) return null;
  const plots = specialPlots.plots || {};
  const list = [plots.crucible, plots.nexus].filter(Boolean);
  if (list.length === 0) return null;

  return (
    <div className="mt-8 mb-6" data-testid="special-plots-section">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-neutral-500">
            🧪🧬 Special Cultivation Plots
          </div>
          <div className="text-sm text-neutral-400 mt-1">
            {specialPlots.unlocked_globally
              ? "Experimental soil active — advanced workshop cultivation & biological splicing available."
              : `Auto-unlocks at Farm Lv ${specialPlots.unlock_farm_level} (${(specialPlots.unlock_xp || 0).toLocaleString()} XP · currently Lv ${specialPlots.farm_level}).`}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.map((p) => (
          <SpecialPlotCard
            key={p.id}
            plot={p}
            busy={busy}
            tick={tick}
            onUpgradeMutation={() => onUpgradeMutation(p.id)}
            onOpenRecipePicker={() => onOpenRecipePicker(p.id)}
            onHarvestSpecial={() => onHarvestSpecial(p.id)}
          />
        ))}
      </div>
    </div>
  );
}

function SpecialPlotCard({ plot, busy, tick, onUpgradeMutation, onOpenRecipePicker, onHarvestSpecial }) {
  const isCrucible = plot.id === "crucible";
  const themeBorder = isCrucible
    ? "border-amber-700/40 shadow-[inset_0_0_30px_rgba(120,53,15,0.25)]"
    : "border-emerald-600/40 shadow-[inset_0_0_30px_rgba(6,95,70,0.25)]";
  const themeBg = isCrucible
    ? "bg-gradient-to-br from-[#1a1208] via-[#0A0A0A] to-[#140b04]"
    : "bg-gradient-to-br from-[#0a1a12] via-[#0A0A0A] to-[#04140b]";
  const themeAccent = isCrucible ? "text-amber-300" : "text-emerald-300";
  const themeCorner = isCrucible ? "⚙️" : "🧬";

  if (!plot.unlocked) {
    return (
      <div
        className={`relative rounded-2xl border-2 border-dashed ${themeBorder} ${themeBg} p-5 min-h-[220px] flex flex-col items-center justify-center text-center opacity-70`}
        data-testid={`special-plot-locked-${plot.id}`}
        title={plot.locked_tooltip}
      >
        <div className="absolute top-3 left-4 text-[10px] uppercase tracking-widest text-neutral-500">
          Plot {plot.roman}
        </div>
        <div className="absolute top-3 right-4 text-lg opacity-40">{themeCorner}</div>
        <div className="text-5xl mb-2 relative">
          <span className="opacity-20">{plot.icon}</span>
          <span className="absolute inset-0 flex items-center justify-center text-3xl text-amber-400">
            🔒
          </span>
        </div>
        <div className={`text-base font-bold ${themeAccent} mt-2`} style={{ fontFamily: 'Outfit, sans-serif' }}>
          {plot.label}
        </div>
        <div className="text-[11px] text-neutral-500 mt-2 max-w-xs leading-relaxed px-2">
          {plot.locked_tooltip}
        </div>
      </div>
    );
  }

  const prog = plot.progression || {};
  const mut = plot.mutation || {};
  const nextMut = mut.next;
  const cp = plot.current_planting || null;

  // 🔴 Live countdown — recompute seconds_remaining from ready_at ISO on every tick.
  // The server sends seconds_remaining as a static snapshot; we tick it down locally
  // so the "Growing (3h 59m)" badge decrements without page reload.
  let liveSecondsRemaining = cp?.seconds_remaining || 0;
  let liveRipe = !!cp?.ripe;
  if (cp && cp.ready_at) {
    const ready = Date.parse(cp.ready_at);
    if (!Number.isNaN(ready)) {
      const remaining = Math.floor((ready - Date.now()) / 1000);
      liveSecondsRemaining = Math.max(0, remaining);
      liveRipe = remaining <= 0;
    }
  }
  // Reference tick to force re-render every second (React sees the changing prop).
  // Prevents "unused variable" warnings without affecting logic.
  void tick;

  // Progress bar within current plot-level tier
  const curLevelBase = prog.level > 0
    ? (plot.progression?.next?.at_xp ? [5000, 25000, 100000, 250000, 500000][prog.level - 1] : 0)
    : 0;
  const nextTarget = prog.next?.at_xp || null;
  const progressPct = nextTarget && nextTarget > curLevelBase
    ? Math.max(0, Math.min(100, ((prog.xp - curLevelBase) / (nextTarget - curLevelBase)) * 100))
    : (prog.level >= 5 ? 100 : 0);

  // Action button state — empty=plant, growing=disabled, ripe=harvest
  let actionLabel = `${plot.action_icon} ${plot.action_label}`;
  let actionOnClick = onOpenRecipePicker;
  let actionDisabled = busy;
  let actionTitle = "Choose a recipe to cultivate on this plot.";
  if (cp) {
    if (liveRipe) {
      actionLabel = `🎉 Harvest ${cp.yield_icon || ""}`;
      actionOnClick = onHarvestSpecial;
      actionTitle = `Harvest your ${cp.yield_label}.`;
    } else {
      actionLabel = `⏳ Growing (${fmt(liveSecondsRemaining)})`;
      actionOnClick = undefined;
      actionDisabled = true;
      actionTitle = `${cp.recipe_label} — ripens in ${fmt(liveSecondsRemaining)}.`;
    }
  }

  return (
    <div
      className={`relative rounded-2xl border-2 ${themeBorder} ${themeBg} p-5 min-h-[220px]`}
      data-testid={`special-plot-unlocked-${plot.id}`}
    >
      {/* Corner decorations */}
      <div className="absolute top-2 left-2 text-lg opacity-30">{themeCorner}</div>
      <div className="absolute top-2 right-2 text-lg opacity-30">{themeCorner}</div>
      <div className="absolute bottom-2 left-2 text-lg opacity-30">{themeCorner}</div>
      <div className="absolute bottom-2 right-2 text-lg opacity-30">{themeCorner}</div>

      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            Plot {plot.roman}
          </div>
          <div className={`text-xl font-bold ${themeAccent}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            {plot.icon} {plot.label}
          </div>
          <div className="text-[11px] text-neutral-500 mt-0.5">
            Lv {prog.level} · <span className="italic">{prog.title}</span>
          </div>
        </div>
        <button
          disabled={actionDisabled}
          className={`px-3 py-2 rounded-lg text-xs font-bold border ${
            liveRipe
              ? "border-amber-400 bg-amber-500/30 text-amber-100 hover:bg-amber-500/40 shadow-[0_0_16px_rgba(245,158,11,0.35)]"
              : isCrucible
                ? "border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
                : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
          } disabled:opacity-40 disabled:cursor-not-allowed transition`}
          data-testid={`special-plot-action-${plot.id}`}
          title={actionTitle}
          onClick={actionOnClick}
        >
          {actionLabel}
        </button>
      </div>

      {/* Currently-growing recipe strip */}
      {cp && (
        <div
          className={`mb-3 rounded-lg border p-3 text-xs ${
            liveRipe
              ? "border-amber-400/60 bg-amber-500/10"
              : "border-neutral-800 bg-black/40"
          }`}
          data-testid={`special-plot-current-${plot.id}`}
        >
          <div className="flex items-center justify-between">
            <div className="font-bold text-neutral-200">
              {cp.yield_icon} {cp.recipe_label}
            </div>
            <div className={liveRipe ? "text-amber-300 font-bold" : "text-emerald-300"}>
              {liveRipe ? "✨ RIPE" : `⏳ ${fmt(liveSecondsRemaining)}`}
            </div>
          </div>
          <div className="text-[10px] text-neutral-500 mt-1">
            Yields <span className="text-neutral-300">{cp.yield_label}</span> on harvest.
          </div>
        </div>
      )}

      {/* Plot XP progress */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] text-neutral-500 mb-1">
          <span>Plot XP</span>
          <span data-testid={`special-plot-xp-${plot.id}`}>
            {prog.xp.toLocaleString()}
            {prog.next && ` / ${prog.next.at_xp.toLocaleString()}`}
            {!prog.next && " (MAX)"}
          </span>
        </div>
        <div className="h-2 rounded-full bg-black/50 overflow-hidden">
          <div
            className={`h-full ${
              isCrucible ? "bg-gradient-to-r from-amber-600 to-amber-400" : "bg-gradient-to-r from-emerald-600 to-emerald-400"
            } transition-all`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        {prog.next && (
          <div className="text-[10px] text-neutral-500 mt-1">
            Next: <span className={themeAccent}>{prog.next.title}</span> · {prog.next.xp_to_go.toLocaleString()} XP to go
          </div>
        )}
      </div>

      {/* Mutation tier */}
      <div className="mt-3 rounded-lg border border-neutral-800 bg-black/40 p-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">
              ⚡ Mutation Rate
            </div>
            <div className={`text-lg font-bold ${themeAccent}`} data-testid={`special-plot-mutation-${plot.id}`}>
              {mut.rate_percent}% <span className="text-xs text-neutral-500 font-normal">· {mut.label}</span>
            </div>
          </div>
          {nextMut && (
            <button
              disabled={busy || !nextMut.affordable_now}
              onClick={onUpgradeMutation}
              className={`px-3 py-2 rounded-lg text-xs font-bold border transition ${
                nextMut.affordable_now
                  ? (isCrucible
                      ? "border-amber-500 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30"
                      : "border-emerald-500 bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30")
                  : "border-neutral-800 bg-black/40 text-neutral-500 cursor-not-allowed"
              }`}
              data-testid={`special-plot-upgrade-mutation-${plot.id}`}
            >
              → {nextMut.rate_percent}%
            </button>
          )}
        </div>
        {nextMut && (
          <div className="text-[10px] text-neutral-500 leading-relaxed">
            <span className={nextMut.plot_level_ok ? "text-emerald-400" : "text-red-400"}>
              {nextMut.plot_level_ok ? "✓" : "✗"} Plot Lv {nextMut.required_plot_level}
            </span>
            <span className="mx-2">·</span>
            <span className={nextMut.currency_ok ? "text-emerald-400" : "text-red-400"}>
              {nextMut.currency_ok ? "✓" : "✗"} {nextMut.cost_chits.toLocaleString()} 🪙
              {nextMut.cost_omni_s > 0 && ` + ${nextMut.cost_omni_s} Omni S`}
            </span>
          </div>
        )}
        {!nextMut && (
          <div className="text-[10px] text-amber-400 mt-1">
            🏆 MAX tier reached — 64% mutation on every harvest.
          </div>
        )}
      </div>

      {/* Description strip */}
      <div className="text-[10px] text-neutral-500 italic mt-3 leading-relaxed">
        {plot.description}
      </div>
    </div>
  );
}

/* ───────── Plot card ───────── */
function PlotCard({ plot, tick, busy, onPlant, onHarvest, onChore, onThrow, onApplyCoolant, onSelfRevive, sporeCount = 0 }) {
  // Recompute remaining countdown by combining seconds_to_X with the tick.
  const elapsed = tick; // not used — placeholder for future client-side smoothing
  const status = plot.status || "empty";
  const seed = plot.seed || {};
  const crop = plot.crop || {};

  // Live countdown — relies on server seconds_to_X snapshots, ticks down locally.
  const ripeSecs = Math.max(0, (plot.seconds_to_ripe || 0) - elapsed);
  const wiltSecs = Math.max(0, (plot.seconds_to_wilt || 0) - elapsed);
  const reviveSecs = Math.max(0, (plot.seconds_to_revive_deadline || 0) - elapsed);

  const ringPct = useMemo(() => {
    if (status === "growing" && seed.grow_hours) {
      const total = seed.grow_hours * 3600;
      return Math.max(0, Math.min(100, 100 - (ripeSecs / total) * 100));
    }
    if (status === "ripe" && seed.grow_hours) {
      const wiltTotal = seed.grow_hours * 2 * 3600;
      return Math.max(0, Math.min(100, 100 - (wiltSecs / wiltTotal) * 100));
    }
    return 0;
  }, [status, ripeSecs, wiltSecs, seed.grow_hours]);

  const borderColor = {
    empty: "border-neutral-800",
    growing: "border-emerald-500/40",
    ripe: "border-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.35)]",
    wilted: "border-red-600/60",
    fissure: "border-purple-600/60 shadow-[0_0_18px_rgba(168,85,247,0.35)]",
  }[status];

  return (
    <div className={`rounded-xl border-2 ${borderColor} bg-gradient-to-br from-black/60 to-[#0e0a14] p-3 min-h-[180px] flex flex-col`}
         data-testid={`plot-${plot.plot_id}`}>
      {/* Status + countdown */}
      <div className="flex items-start justify-between text-[10px]">
        <span className="px-2 py-0.5 rounded-full border border-neutral-700 uppercase tracking-wider text-neutral-400"
              data-testid={`plot-status-${plot.plot_id}`}>
          {status}
        </span>
        {status === "growing" && <span className="text-emerald-300">🌱 {fmt(ripeSecs)}</span>}
        {status === "ripe"    && <span className="text-amber-300">✨ wilts in {fmt(wiltSecs)}</span>}
        {status === "wilted"  && <span className="text-red-400">💀 revive in {fmt(reviveSecs)}</span>}
      </div>

      {/* Visual */}
      <div className="flex-1 flex items-center justify-center my-2">
        {status === "empty" && <span className="text-5xl opacity-40">🟫</span>}
        {status === "growing" && (
          <div className="text-center">
            <div className="text-5xl">{seed.icon || "🌱"}</div>
            <div className="mt-2 h-1.5 w-24 mx-auto rounded-full bg-neutral-800 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${ringPct}%` }} />
            </div>
          </div>
        )}
        {status === "ripe" && (
          <div className="text-center">
            <div className="text-5xl animate-bounce">{seed.icon || "✨"}</div>
            <div className="mt-1 text-amber-400 text-xs font-bold">READY!</div>
          </div>
        )}
        {status === "wilted" && <div className="text-5xl grayscale opacity-50">💀</div>}
        {status === "fissure" && (
          <div className="text-center">
            <div className="text-5xl">🌋</div>
            <div className="mt-1 text-purple-300 text-[10px] font-bold uppercase tracking-wider">
              Scorched Fissure
            </div>
            <div className="text-[9px] text-neutral-500 mt-0.5 max-w-[140px]">
              Apply 🌿 Coolant or 🧊 Cooling Agent to restore.
            </div>
          </div>
        )}
      </div>

      {/* Action row */}
      <div className="flex flex-wrap gap-1 text-xs">
        {status === "empty" && (
          <button data-testid={`plot-plant-${plot.plot_id}`}
                  disabled={busy} onClick={onPlant}
                  className="flex-1 px-2 py-1.5 rounded bg-emerald-700/70 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold">
            🌱 Plant
          </button>
        )}
        {status === "growing" && (
          <>
            <button data-testid={`plot-water-${plot.plot_id}`}
                    disabled={busy} onClick={() => onChore("water")}
                    className="px-2 py-1.5 rounded bg-blue-700/70 hover:bg-blue-600 disabled:opacity-50">
              💧
            </button>
            <button data-testid={`plot-deworm-${plot.plot_id}`}
                    disabled={busy} onClick={() => onChore("deworm")}
                    className="px-2 py-1.5 rounded bg-purple-700/70 hover:bg-purple-600 disabled:opacity-50">
              🪱
            </button>
            <button data-testid={`plot-weed-${plot.plot_id}`}
                    disabled={busy} onClick={() => onChore("weed")}
                    className="px-2 py-1.5 rounded bg-amber-700/70 hover:bg-amber-600 disabled:opacity-50">
              🌿
            </button>
          </>
        )}
        {status === "ripe" && (
          <button data-testid={`plot-harvest-${plot.plot_id}`}
                  disabled={busy} onClick={onHarvest}
                  className="flex-1 px-2 py-1.5 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold">
            ✨ Harvest
          </button>
        )}
        {status === "wilted" && (
          <>
            {sporeCount > 0 && onSelfRevive && (
              <button data-testid={`plot-lazarus-${plot.plot_id}`}
                      disabled={busy} onClick={onSelfRevive}
                      className="flex-1 px-2 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold border border-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.35)]"
                      title={`Consume 1 Lazarus Spore (${sporeCount} owned) to revive this crop yourself.`}>
                🌿 Revive ({sporeCount})
              </button>
            )}
            <button data-testid={`plot-throw-${plot.plot_id}`}
                    disabled={busy} onClick={onThrow}
                    className="flex-1 px-2 py-1.5 rounded bg-red-700/70 hover:bg-red-600 disabled:opacity-50 text-white font-bold">
              🗑️ Throw Away
            </button>
          </>
        )}
        {status === "fissure" && (
          <button data-testid={`plot-coolant-${plot.plot_id}`}
                  disabled={busy} onClick={onApplyCoolant}
                  className="flex-1 px-2 py-1.5 rounded bg-purple-700/70 hover:bg-purple-600 disabled:opacity-50 text-white font-bold">
            🌿 Apply Coolant
          </button>
        )}
      </div>
    </div>
  );
}

/* ───────── Seed Shop ───────── */
function SeedShop({ state, setState, busy, setBusy, refresh }) {
  // Dynamically init qty for ALL catalog seeds (Lv 1 + Lv 2)
  const initialQty = Object.keys(state.seeds || {}).reduce((acc, k) => { acc[k] = 1; return acc; }, {});
  const [qty, setQty] = useState(initialQty);
  const farmLevel = state.farm_level || 1;
  const onBuy = async (seed_type) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/buy-seed`, { seed_type, qty: qty[seed_type] || 1 });
      // 🩹 Oct 2026 (Fix #31) — Owner reported seed shortage: buying 10
      // charged 600 Chits but credited fewer seeds. Backend always wrote
      // correctly; the bug was a stale FE state. Apply the authoritative
      // post-write `seed_inventory` from the server response IMMEDIATELY
      // so the UI can never drift behind the DB, even if `refresh()`
      // races or fails silently.
      if (r?.data?.seed_inventory) {
        setState((prev) => prev ? { ...prev, seed_inventory: r.data.seed_inventory } : prev);
      }
      if (typeof r?.data?.chits_balance === "number") {
        setState((prev) => prev ? { ...prev, chits_balance: r.data.chits_balance } : prev);
      }
      toast.success(`Bought ${qty[seed_type]} ${r.data.seed_label} (-${r.data.spent} 🪙)`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Buy failed.");
    } finally {
      setBusy(false);
    }
  };
  // Sort: unlocked Lv 1 first, then locked Lv 2 grouped at the bottom
  const seedList = Object.values(state.seeds || {}).sort((a, b) => {
    const aReq = a.unlock_farm_level || 0;
    const bReq = b.unlock_farm_level || 0;
    if (aReq !== bReq) return aReq - bReq;
    return (a.cost_chits || 0) - (b.cost_chits || 0);
  });
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {seedList.map((s) => {
        const owned = (state.seed_inventory || {})[s.id] || 0;
        const requiredLv = s.unlock_farm_level || 0;
        const locked = requiredLv > 0 && farmLevel < requiredLv;
        const isTier2 = s.tier === 2 || s.id === "bullion";
        return (
          <div key={s.id}
               className={`rounded-xl border p-4 relative ${
                 locked
                   ? "border-neutral-900 bg-black/40 grayscale opacity-60"
                   : isTier2
                     ? "border-amber-500/60 bg-gradient-to-br from-amber-950/40 to-[#1a0a20] shadow-[0_0_18px_rgba(245,158,11,0.15)]"
                     : "border-neutral-800 bg-gradient-to-br from-black/60 to-[#1a0a20]"
               }`}
               data-testid={`shop-card-${s.id}`}>
            {isTier2 && !locked && (
              <div className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-amber-500 text-black text-[10px] font-black tracking-wide" data-testid={`shop-tier2-badge-${s.id}`}>
                LV 2
              </div>
            )}
            {locked && (
              <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-red-900/70 text-red-200 text-[10px] font-bold" data-testid={`shop-locked-${s.id}`}>
                🔒 Farm Lv {requiredLv}
              </div>
            )}
            <div className="text-5xl text-center mb-2">{s.icon}</div>
            <div className="text-center font-bold text-amber-200 text-lg">{s.label}</div>
            <div className="text-center text-xs text-neutral-400 mt-1">
              Grows in {s.grow_hours}h · Wilts after {s.grow_hours * 2}h
            </div>
            <div className="text-center text-sm mt-2">
              Drops: <span className="text-amber-300">{s.yield_icon} {s.yield_label} × {s.base_yield.toLocaleString()}</span>
              {s.daves_egg_chance && (
                <span className="text-purple-300"> · {Math.round(s.daves_egg_chance * 100)}% 👨‍🍳 Dave's Egg</span>
              )}
            </div>
            <div className="text-center text-xs text-neutral-500 mt-2">You have: {owned}</div>
            <div className="mt-4 flex items-center gap-2">
              <input type="number" min={1} max={50} value={qty[s.id] || 1}
                     data-testid={`shop-qty-${s.id}`}
                     disabled={locked}
                     onChange={(e) => setQty((q) => ({ ...q, [s.id]: Math.max(1, Math.min(50, parseInt(e.target.value) || 1)) }))}
                     className="w-16 px-2 py-1.5 rounded bg-black/60 border border-neutral-700 text-center disabled:opacity-50" />
              <button data-testid={`shop-buy-${s.id}`}
                      disabled={busy || locked} onClick={() => onBuy(s.id)}
                      className="flex-1 px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-sm">
                {locked ? `🔒 Lv ${requiredLv}` : `🪙 ${(s.cost_chits * (qty[s.id] || 1)).toLocaleString()}`}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ───────── Friend Farms ───────── */
function FriendFarms({ friends, tick, busy, setBusy, refresh, refreshMine }) {
  const onRevive = async (farm_owner_id, plot_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/revive-friend`, { farm_owner_id, plot_id });
      toast.success(`🤝 Revived! +${r.data.xp_to_friend} Farm XP to you and your friend.`);
      refresh();
      refreshMine();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revive failed.");
    } finally {
      setBusy(false);
    }
  };
  const onReviveAll = async (farm_owner_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/revive-all`, { farm_owner_id });
      const n = r.data.revived_count || 0;
      if (n === 0) {
        toast.info("No wilted crops to revive here.");
      } else {
        toast.success(`🤝 Revived ${n} crop${n === 1 ? "" : "s"}! +${r.data.xp_to_friend} Farm XP each side.`);
      }
      refresh();
      refreshMine();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revive-all failed.");
    } finally {
      setBusy(false);
    }
  };
  const onFriendChore = async (farm_user_id, plot_id, chore_type) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/chore`, { farm_user_id, plot_id, chore_type });
      toast.success(`+${r.data.xp_awarded} Farm XP (${r.data.chore_count_pt}/${r.data.chore_daily_cap})`);
      refresh();
      refreshMine();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chore failed.");
    } finally {
      setBusy(false);
    }
  };

  if (friends.length === 0) {
    return (
      <div className="text-center py-12 text-neutral-500" data-testid="friend-farms-empty">
        <div className="text-5xl mb-3">👥</div>
        Add some friends to revive their wilted crops and grind +1 Farm XP per chore.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="friend-farms-list">
      {friends.map((f) => (
        <div key={f.user_id} className="rounded-xl border border-neutral-800 bg-black/30 p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="font-bold text-amber-200" data-testid={`friend-name-${f.user_id}`}>
                {f.name}{f.handle ? ` · @${f.handle}` : ""}
              </div>
              <div className="text-xs text-neutral-500">
                Farm XP {f.farm_xp} · Soil Lv {f.soil_level}
              </div>
            </div>
            <div className="flex gap-2 text-xs">
              {f.wilted_count > 0 && (
                <>
                  <span className="px-2 py-1 rounded-full bg-red-600/30 border border-red-500/50 text-red-200">
                    💀 {f.wilted_count} wilted
                  </span>
                  <button
                    onClick={() => onReviveAll(f.user_id)}
                    disabled={busy}
                    data-testid={`revive-all-${f.user_id}`}
                    className="px-2 py-1 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-[11px] font-bold"
                    title="Bulk revive every wilted crop on this farm"
                  >
                    🤝 Revive All
                  </button>
                </>
              )}
              {f.ripe_count > 0 && (
                <span className="px-2 py-1 rounded-full bg-amber-600/20 border border-amber-500/40 text-amber-200">
                  ✨ {f.ripe_count} ripe
                </span>
              )}
              {f.growing_count > 0 && (
                <span className="px-2 py-1 rounded-full bg-emerald-600/20 border border-emerald-500/40 text-emerald-200">
                  🌱 {f.growing_count} growing
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {f.plots.filter((p) => p.status !== "empty").map((p) => (
              <FriendPlotCard key={p.plot_id} plot={p} tick={tick} busy={busy}
                              onRevive={() => onRevive(f.user_id, p.plot_id)}
                              onChore={(t) => onFriendChore(f.user_id, p.plot_id, t)} />
            ))}
            {f.plots.filter((p) => p.status !== "empty").length === 0 && (
              <div className="col-span-full text-xs text-neutral-500 italic">No active plots right now.</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function FriendPlotCard({ plot, tick, busy, onRevive, onChore }) {
  const status = plot.status;
  const seed = plot.seed || {};
  const ripeSecs = Math.max(0, (plot.seconds_to_ripe || 0) - tick);
  const wiltSecs = Math.max(0, (plot.seconds_to_wilt || 0) - tick);
  const reviveSecs = Math.max(0, (plot.seconds_to_revive_deadline || 0) - tick);
  return (
    <div className={`rounded-lg border p-2 text-xs ${
      status === "wilted" ? "border-red-600/50 bg-red-950/20" :
      status === "ripe" ? "border-amber-500/40" : "border-neutral-800 bg-black/40"
    }`}>
      <div className="flex items-center justify-between">
        <span className="text-xl">{status === "wilted" ? "💀" : seed.icon || "🌱"}</span>
        <span className="text-[10px] uppercase text-neutral-500">{status}</span>
      </div>
      <div className="text-[10px] text-neutral-400 mt-1">
        {status === "growing" && `🌱 ${fmt(ripeSecs)}`}
        {status === "ripe" && `✨ wilts in ${fmt(wiltSecs)}`}
        {status === "wilted" && `💀 ${fmt(reviveSecs)} left`}
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {status === "wilted" && (
          <button data-testid={`friend-revive-${plot.plot_id}`}
                  disabled={busy} onClick={onRevive}
                  className="flex-1 px-2 py-1 rounded bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-[11px] font-bold">
            🤝 Revive
          </button>
        )}
        {status === "growing" && (
          <>
            <button data-testid={`friend-water-${plot.plot_id}`}
                    disabled={busy} onClick={() => onChore("water")}
                    className="px-1.5 py-1 rounded bg-blue-700/60 hover:bg-blue-600 disabled:opacity-50">💧</button>
            <button data-testid={`friend-deworm-${plot.plot_id}`}
                    disabled={busy} onClick={() => onChore("deworm")}
                    className="px-1.5 py-1 rounded bg-purple-700/60 hover:bg-purple-600 disabled:opacity-50">🪱</button>
            <button data-testid={`friend-weed-${plot.plot_id}`}
                    disabled={busy} onClick={() => onChore("weed")}
                    className="px-1.5 py-1 rounded bg-amber-700/60 hover:bg-amber-600 disabled:opacity-50">🌿</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ───────── Soil Upgrades ───────── */
function SoilUpgrades({ state, busy, setBusy, refresh }) {
  const next = state.next_soil;
  const onUpgrade = async () => {
    if (busy || !next) return;
    const ok = await confirmDialog({
      title: `Upgrade Soil to Lv ${next.level}?`,
      message: `Cost: ${next.od_cost} 🪙 Omni Dollars\nUnlocks: ${next.label} (${next.yield_mult}× yield)`,
      confirmLabel: "Upgrade",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/upgrade-soil`);
      toast.success(`🪨 ${r.data.soil_label} unlocked! ${r.data.yield_mult}× yield`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {Object.values(state.soil_levels || {}).map((s) => {
          const owned = state.soil_level >= s.level;
          const isNext = state.soil_level + 1 === s.level;
          return (
            <div key={s.level}
                 className={`rounded-xl border-2 p-3 text-center ${
                   owned ? "border-amber-400 bg-amber-500/10" :
                   isNext ? "border-purple-500 bg-purple-500/10" :
                   "border-neutral-800 bg-black/30 opacity-60"
                 }`}
                 data-testid={`soil-lv-${s.level}`}>
              <div className="text-3xl">{owned ? "✅" : isNext ? "🪨" : "🔒"}</div>
              <div className="font-bold text-sm mt-1">Lv {s.level}</div>
              <div className="text-[10px] text-neutral-400 leading-tight">{s.label.replace(`Soil Lv ${s.level} — `, "")}</div>
              <div className="text-amber-200 text-sm font-bold mt-2">{s.yield_mult}× yield</div>
              {s.level > 1 && (
                <div className="text-[10px] text-neutral-500 mt-1">{s.od_cost} 🪙 OD</div>
              )}
            </div>
          );
        })}
      </div>
      {next ? (
        <div className="mt-6 text-center">
          <button data-testid="upgrade-soil-btn"
                  disabled={busy || state.wallet.omni_dollars < next.od_cost}
                  onClick={onUpgrade}
                  className="px-6 py-3 rounded-lg bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold">
            🪨 Upgrade to Lv {next.level} — {next.od_cost} 🪙 OD
          </button>
          <div className="text-xs text-neutral-500 mt-2">
            You have {state.wallet.omni_dollars} 🪙 OD · Next yield: {next.yield_mult}×
          </div>
        </div>
      ) : (
        <div className="mt-6 text-center text-amber-300 font-bold" data-testid="soil-max">
          🏆 Soil at MAX level — yield is permanently {state.soil_yield_mult}×
        </div>
      )}
    </div>
  );
}

/* ───────── 🌽🫘🎃 Three Sisters Workshop ───────── */
function WorkshopPanel({ busy, setBusy, refreshFarm }) {
  const [ws, setWs] = useState(null);

  const loadWs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/farm/workshop/state`);
      setWs(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load workshop.");
    }
  }, []);

  useEffect(() => { loadWs(); }, [loadWs]);

  const onCraft = async (recipe_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/workshop/craft`, { recipe_id });
      toast.success(`🌿 Crafted ${r.data.crafted}× ${r.data.output_label || "item"}!`);
      await loadWs();
      refreshFarm?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Craft failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!ws) return <div className="text-center text-neutral-500 py-8">Loading workshop…</div>;

  return (
    <div data-testid="workshop-panel">
      {/* Header strip */}
      <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-[#1a0820] via-[#0A0A0A] to-[#1a1208] p-5 mb-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-black tracking-wide" style={{ fontFamily: 'Anton, sans-serif' }}>
              🛠️ <span className="text-purple-300">FARM WORKSHOP</span>
            </div>
            <div className="text-xs text-neutral-400 mt-1 max-w-xl">
              Combine 🌽 Corn + 🫘 Pole Beans + 🎃 Squash into the <span className="text-emerald-300">Three Sisters Coolant</span> —
              an organic remedy that restores 🌋 Scorched Earth Fissures without battle-loot.
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Pill icon="🌿" label="Coolants" value={ws.coolants_owned} testid="ws-coolants" />
            <Pill icon="🌋" label="Fissures" value={ws.fissure_count} testid="ws-fissures" />
            <Pill icon="🪙" label="Chits" value={ws.chits} testid="ws-chits" />
          </div>
        </div>
      </div>

      {/* Materials row */}
      <div className="rounded-xl border border-neutral-800 bg-black/30 p-4 mb-5">
        <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
          🌱 Materials (from your harvests)
        </div>
        <div className="flex flex-wrap gap-3">
          {(ws.materials || []).map((m) => (
            <div key={m.id}
                 className="px-3 py-2 rounded-lg border border-neutral-700 bg-black/40 text-sm flex items-center gap-2"
                 data-testid={`ws-mat-${m.id}`}>
              <span className="text-xl">{m.icon}</span>
              <div>
                <div className="font-bold text-amber-200">{m.owned}</div>
                <div className="text-[10px] text-neutral-500">{m.label}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-neutral-500 mt-3 leading-snug">
          Out of stock? Plant 🌽 Corn, 🫘 Pole Beans, or 🎃 Squash from the Seed Shop and harvest.
        </div>
      </div>

      {/* Recipes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(ws.recipes || []).map((r) => (
          <div key={r.recipe_id}
               className="rounded-xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-900/15 to-black/60 p-4"
               data-testid={`ws-recipe-${r.recipe_id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="font-bold text-emerald-200 text-base">
                  {r.icon} {r.name}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-0.5">
                  {r.rarity} · {r.category?.replace(/_/g, " ")}
                </div>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/30 text-emerald-300">
                +{r.output_qty} {r.output_label}
              </span>
            </div>
            <p className="text-xs text-neutral-400 leading-relaxed mb-3">{r.lore}</p>
            <div className="text-[11px] text-neutral-500 uppercase tracking-wider mb-1">Requires</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {Object.entries(r.materials || {}).map(([k, v]) => {
                const matMeta = (ws.materials || []).find((m) => m.id === k) || {};
                const owned = matMeta.owned || 0;
                const have = owned >= v;
                return (
                  <span key={k}
                        className={`px-2 py-1 rounded border text-xs ${
                          have ? "border-emerald-600/50 text-emerald-200"
                               : "border-red-600/50 text-red-300"
                        }`}
                        data-testid={`ws-req-${r.recipe_id}-${k}`}>
                    {matMeta.icon || "•"} {v} {matMeta.label || k} <span className="opacity-60">({owned})</span>
                  </span>
                );
              })}
              {r.chits_fee > 0 && (
                <span className={`px-2 py-1 rounded border text-xs ${
                  ws.chits >= r.chits_fee ? "border-amber-500/50 text-amber-200"
                                          : "border-red-600/50 text-red-300"
                }`}>
                  🪙 {r.chits_fee} Chits
                </span>
              )}
            </div>
            <button data-testid={`ws-craft-${r.recipe_id}`}
                    disabled={busy || !r.can_craft}
                    onClick={() => onCraft(r.recipe_id)}
                    className="w-full px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold transition">
              {r.can_craft ? `🌿 Craft ${r.name}` : "🔒 Not enough materials"}
            </button>
          </div>
        ))}
      </div>

      {/* Footer nudge */}
      {ws.fissure_count > 0 && ws.coolants_owned > 0 && (
        <div className="mt-5 rounded-xl border border-purple-500/40 bg-purple-900/15 p-4 text-sm text-purple-200"
             data-testid="ws-fissure-nudge">
          🌋 You have <b>{ws.fissure_count}</b> scorched fissure{ws.fissure_count !== 1 ? "s" : ""} and <b>{ws.coolants_owned}</b> coolant{ws.coolants_owned !== 1 ? "s" : ""} ready.
          Head to <b>🌱 Your Farm</b> and tap any 🌋 plot to apply.
        </div>
      )}
    </div>
  );
}


/* ───────── 🧪🧬 Special Recipe Picker Modal ─────────
 * Modal for selecting a recipe (Humic Acid Vial / De-o-ha-ko Resuscitator)
 * to plant on Plot XI (Crucible) or Plot XII (Nexus).
 */
function SpecialRecipePickerModal({ plot, onClose, onPlant, busy, wallet }) {
  if (!plot) return null;
  const recipes = plot.recipes || [];
  const isCrucible = plot.id === "crucible";
  const accent = isCrucible ? "text-amber-300" : "text-emerald-300";
  const border = isCrucible ? "border-amber-700/40" : "border-emerald-600/40";
  const bg = isCrucible
    ? "bg-gradient-to-br from-[#1a1208] via-[#0A0A0A] to-[#140b04]"
    : "bg-gradient-to-br from-[#0a1a12] via-[#0A0A0A] to-[#04140b]";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid={`special-recipe-modal-${plot.id}`}
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-2xl rounded-2xl border-2 ${border} ${bg} p-6 shadow-2xl max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-3 right-4 text-neutral-400 hover:text-white text-2xl leading-none"
          onClick={onClose}
          data-testid={`special-recipe-modal-close-${plot.id}`}
        >
          ×
        </button>

        <div className="mb-2">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            Plot {plot.roman} · {plot.label}
          </div>
          <div className={`text-2xl font-bold ${accent}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            {plot.icon} {plot.action_label} a Recipe
          </div>
        </div>

        <div className="text-xs text-neutral-500 mb-4">
          🪙 {(wallet?.chits || 0).toLocaleString()} Chits available
        </div>

        {recipes.length === 0 && (
          <div className="p-6 text-center text-neutral-400 text-sm border border-dashed border-neutral-700 rounded-lg">
            No recipes wired for this plot yet. Coming in a later phase.
          </div>
        )}

        <div className="space-y-3">
          {recipes.map((r) => (
            <div
              key={r.recipe_id}
              className={`rounded-xl border-2 ${border} bg-black/40 p-4`}
              data-testid={`special-recipe-${plot.id}-${r.recipe_id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className={`text-lg font-bold ${accent}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {r.icon} {r.label}
                  </div>
                  <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">
                    {r.description}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase text-neutral-500">Grow</div>
                  <div className="text-sm font-bold text-neutral-200">{r.grow_hours}h</div>
                </div>
              </div>

              {/* Costs */}
              <div className="flex flex-wrap gap-2 mb-3">
                {r.cost_chits > 0 && (
                  <span className={`px-2 py-1 rounded border text-[11px] ${
                    r.chits_ok ? "border-amber-500/50 text-amber-200 bg-amber-500/10"
                              : "border-red-600/50 text-red-300 bg-red-900/10"
                  }`}>
                    🪙 {r.cost_chits} Chits
                  </span>
                )}
                {Object.entries(r.cost_harvest || {}).map(([k, v]) => (
                  <span key={k} className={`px-2 py-1 rounded border text-[11px] ${
                    (r._have_harvest?.[k] || 0) >= v
                      ? "border-emerald-500/50 text-emerald-200 bg-emerald-500/10"
                      : "border-red-600/50 text-red-300 bg-red-900/10"
                  }`}>
                    {v}× {k} ({r._have_harvest?.[k] || 0}/{v})
                  </span>
                ))}
                {Object.entries(r.cost_special || {}).map(([k, v]) => (
                  <span key={k} className={`px-2 py-1 rounded border text-[11px] ${
                    (r._have_special?.[k] || 0) >= v
                      ? "border-emerald-500/50 text-emerald-200 bg-emerald-500/10"
                      : "border-red-600/50 text-red-300 bg-red-900/10"
                  }`}>
                    {v}× {k} ({r._have_special?.[k] || 0}/{v})
                  </span>
                ))}
              </div>

              {/* Yield */}
              <div className="text-[11px] text-neutral-400 mb-3">
                Yields: <span className="text-neutral-200 font-bold">{r.yield_icon} {r.yield_qty}× {r.yield_label}</span>
              </div>

              <button
                disabled={busy || !r.affordable_now}
                onClick={() => onPlant(r.recipe_id)}
                className={`w-full px-4 py-2.5 rounded-lg text-sm font-bold transition ${
                  r.affordable_now
                    ? (isCrucible
                        ? "bg-amber-500 hover:bg-amber-400 text-black"
                        : "bg-emerald-500 hover:bg-emerald-400 text-black")
                    : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                } disabled:opacity-50`}
                data-testid={`special-recipe-plant-${plot.id}-${r.recipe_id}`}
              >
                {r.affordable_now ? `🌱 Plant ${r.label}` : "🔒 Missing materials"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ═════════════════ 👨‍🌾 Ranchhand Panel ═════════════════ */
function RanchhandPanel({ refreshFarm, isVIP, initialRanchhand }) {
  const tickSec = useTick();
  const [tiers, setTiers] = useState([]);
  const [active, setActive] = useState(initialRanchhand || null);
  const [boundOD, setBoundOD] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedTier, setSelectedTier] = useState("shift");

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/farm/ranchhand/status`);
      const d = r.data || {};
      setTiers(d.tiers || []);
      setActive(d.active || null);
      setBoundOD(d.bound_omni_dollars || 0);
    } catch {
      /* silent — panel is non-critical */
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const activeCountdown = useMemo(() => {
    if (!active) return null;
    const exp = active.expires_at_utc ? Date.parse(active.expires_at_utc) : null;
    const paused = active.paused_at_utc ? Date.parse(active.paused_at_utc) : null;
    if (!exp) return null;
    const now = Date.now();
    // While paused, remaining is frozen at (exp - paused_at)
    const remaining = paused ? Math.max(0, Math.floor((exp - paused) / 1000))
                             : Math.max(0, Math.floor((exp - now) / 1000));
    return remaining;
  }, [active, tickSec]);

  const isPaused = active?.status === "paused_seeds";
  const isExpired = active?.status === "expired" || (activeCountdown === 0 && !isPaused);
  const isCancelled = active?.status === "cancelled";

  const onHire = async () => {
    if (busy) return;
    const tier = selectedTier;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/farm/ranchhand/hire`, { tier });
      const d = r.data || {};
      const label = d.ranchhand?.label || "Ranchhand";
      if (d.vip_bypass) {
        toast.success(`${label} hired FREE (VIP).`);
      } else {
        toast.success(`${label} hired for ${d.cost_paid} 🎫 Bound OD.`);
      }
      await refresh();
      refreshFarm && refreshFarm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hire failed.");
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async () => {
    if (busy) return;
    if (!window.confirm("Cancel your active Ranchhand? No refund (per canon).")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/farm/ranchhand/cancel`);
      toast.success("Ranchhand released.");
      await refresh();
      refreshFarm && refreshFarm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed.");
    } finally {
      setBusy(false);
    }
  };

  // Compact banner if no active Ranchhand and panel collapsed
  if (!active || isExpired || isCancelled) {
    return (
      <div className="mb-4 rounded-xl border border-amber-900/40 bg-gradient-to-r from-amber-950/40 to-black/40 p-3" data-testid="ranchhand-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-2xl">👨‍🌾</span>
            <div>
              <div className="text-sm font-bold text-amber-200">Farm Ranchhands</div>
              <div className="text-[11px] text-neutral-400">
                {isVIP ? "VIP: hire any tier FREE while active."
                       : `Bound OD: ${boundOD.toLocaleString()} · buy tiers below`}
              </div>
            </div>
          </div>
          <button
            data-testid="ranchhand-toggle-btn"
            onClick={() => setExpanded((v) => !v)}
            className="px-3 py-1.5 rounded-lg text-xs font-bold border border-amber-500 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30 transition">
            {expanded ? "Hide" : "Hire a Ranchhand"}
          </button>
        </div>
        {expanded && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {tiers.map((t) => {
              const affordable = isVIP || boundOD >= t.bound_od_cost;
              const chosen = selectedTier === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`ranchhand-tier-${t.id}`}
                  onClick={() => setSelectedTier(t.id)}
                  className={`text-left rounded-lg border p-3 transition ${
                    chosen ? "border-amber-400 bg-amber-500/10"
                           : "border-neutral-800 bg-black/30 hover:border-neutral-600"
                  } ${!affordable && !isVIP ? "opacity-60" : ""}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-sm text-amber-100">
                      <span className="mr-1">{t.icon}</span>{t.label}
                    </div>
                    <div className="text-[10px] text-neutral-500">{t.duration_hours}h</div>
                  </div>
                  <div className="text-[11px] text-neutral-400 mt-1 leading-tight">{t.description}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-xs">
                      {isVIP ? (
                        <span className="text-emerald-400 font-bold">VIP · FREE</span>
                      ) : (
                        <span className={affordable ? "text-amber-200" : "text-red-400"}>
                          🎫 {t.bound_od_cost.toLocaleString()} Bound OD
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
            <button
              data-testid="ranchhand-hire-confirm"
              disabled={busy || (!isVIP && (tiers.find((x) => x.id === selectedTier)?.bound_od_cost || 0) > boundOD)}
              onClick={onHire}
              className="col-span-full mt-1 px-4 py-2 rounded-lg text-sm font-bold border transition
                         border-amber-500 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30
                         disabled:opacity-40 disabled:cursor-not-allowed">
              {busy ? "Hiring…"
                    : isVIP ? `Hire ${tiers.find((x) => x.id === selectedTier)?.label || ""} · FREE (VIP)`
                            : `Hire ${tiers.find((x) => x.id === selectedTier)?.label || ""} · ${(tiers.find((x) => x.id === selectedTier)?.bound_od_cost || 0).toLocaleString()} Bound OD`}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Active Ranchhand card
  return (
    <div className={`mb-4 rounded-xl border p-3 ${
      isPaused ? "border-orange-500/60 bg-orange-950/30" : "border-emerald-500/50 bg-emerald-950/30"
    }`} data-testid="ranchhand-active-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{active.icon}</span>
          <div>
            <div className="text-sm font-bold text-amber-100">
              {active.label}
              {isPaused && <span className="ml-2 text-orange-300 text-xs font-normal">⏸ Paused — no seeds</span>}
              {!isPaused && <span className="ml-2 text-emerald-300 text-xs font-normal">● Active</span>}
            </div>
            <div className="text-[11px] text-neutral-400">
              Time left: <span className="text-amber-200 font-mono">{fmt(activeCountdown)}</span>
              {" · "}
              Preferred seed: <span className="text-amber-200">{active.preferred_seed}</span>
              {active.harvested_total > 0 && <span className="ml-2 text-emerald-300">🧺 {active.harvested_total}</span>}
              {active.planted_total > 0 && <span className="ml-1 text-teal-300">🌱 {active.planted_total}</span>}
            </div>
          </div>
        </div>
        <button
          data-testid="ranchhand-cancel-btn"
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg text-xs font-bold border border-red-500/60 bg-red-500/10 text-red-200 hover:bg-red-500/20 transition disabled:opacity-40">
          Release
        </button>
      </div>
      {isPaused && (
        <div className="mt-2 text-[11px] text-orange-300">
          Add {active.preferred_seed} seeds to your inventory to auto-resume — the clock is frozen while paused.
        </div>
      )}
    </div>
  );
}
